<template>
  <!-- Set / Choice-bearing Main: hide the Main row and render each Choice as
       its own primary item (qty box + name + that Choice's own modifiers).
       Main-level regular modifiers — if any — render at the top so the kitchen
       still sees them above the Choice list. -->
  <div v-if="hasChoices" class="chit-item-choice-group" :class="{ 'chit-item--voided': isVoided }">
    <div v-if="regularModifiers.length" class="chit-item-choice-group__main-modifiers">
      <ChitModifier
        v-for="(modifier, index) in regularModifiers"
        :key="'m-' + index"
        :modifier="modifier"
        :depth="0"
        :name-field="nameField"
        :show-description="showDescription"
        :description-field="descriptionField"
      />
    </div>
    <p v-if="remark" class="chit-item__remark chit-item-choice-group__main-remark">{{ remark }}</p>
    <div
      v-for="(choice, index) in choices"
      :key="'c-' + index"
      class="chit-item"
      :class="{ 'chit-item--struck': isStruck(choice) }"
    >
      <component
        :is="canStrike(choice) ? 'button' : 'div'"
        :type="canStrike(choice) ? 'button' : undefined"
        class="chit-item__qty-box"
        :class="{ 'chit-item__qty-box--tappable': canStrike(choice) }"
        :aria-pressed="canStrike(choice) ? String(isStruck(choice)) : undefined"
        @click="onQtyTap($event, choice)"
      >
        {{ choiceQuantity(choice) }}
        <i v-if="isStruck(choice)" class="material-icons-round chit-item__qty-check">check</i>
      </component>
      <div class="chit-item__body">
        <h3 class="chit-item__name">{{ primaryTextOf(choice) }}</h3>
        <p
          v-if="descriptionOf(choice)"
          class="chit-item__description"
        >{{ descriptionOf(choice) }}</p>
        <div v-if="choice.children && choice.children.length" class="chit-item__modifiers">
          <ChitModifier
            v-for="(child, ci) in choice.children"
            :key="ci"
            :modifier="child"
            :depth="0"
            :name-field="nameField"
            :show-description="showDescription"
            :description-field="descriptionField"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Regular item: Main name + optional description + modifiers (existing layout). -->
  <div
    v-else
    class="chit-item"
    :class="{ 'chit-item--voided': isVoided, 'chit-item--struck': isStruck(item) }"
  >
    <component
      :is="canStrike(item) ? 'button' : 'div'"
      :type="canStrike(item) ? 'button' : undefined"
      class="chit-item__qty-box"
      :class="{ 'chit-item__qty-box--tappable': canStrike(item) }"
      :aria-pressed="canStrike(item) ? String(isStruck(item)) : undefined"
      @click="onQtyTap($event, item)"
    >
      {{ quantity }}
      <i v-if="isStruck(item)" class="material-icons-round chit-item__qty-check">check</i>
    </component>
    <div class="chit-item__body">
      <h3 class="chit-item__name">{{ primaryTextOf(item) }}</h3>
      <p
        v-if="descriptionOf(item)"
        class="chit-item__description"
      >{{ descriptionOf(item) }}</p>
      <div v-if="hasModifiers" class="chit-item__modifiers">
        <ChitModifier
          v-for="(modifier, index) in modifiers"
          :key="index"
          :modifier="modifier"
          :depth="0"
          :name-field="nameField"
          :show-description="showDescription"
          :description-field="descriptionField"
        />
      </div>
      <p v-if="remark" class="chit-item__remark">{{ remark }}</p>
    </div>
  </div>
</template>

<script>
import ChitModifier from "./ChitModifier.vue";
import { NAME_FIELD_KEYS } from "../kds.utils";

export default {
  name: "OrderChitItem",
  components: {
    ChitModifier,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    // Which of the item's four name columns to print. Defaults to Name 1 so
    // callers outside the KDS flow keep the old behavior. Modifier and Choice
    // rows use the very same field — M939: they used to print whatever name
    // the order snapshot happened to carry.
    nameField: {
      type: String,
      default: "name1",
      validator: (v) => ["name1", "name2", "name3", "name4"].includes(v),
    },
    // Display-both toggle: print a second line under the name.
    showDescription: {
      type: Boolean,
      default: false,
    },
    // Which column feeds that second line: "none" = the item's own
    // Description, otherwise a name column (M942).
    descriptionField: {
      type: String,
      default: "none",
      validator: (v) => ["none", "name1", "name2", "name3", "name4"].includes(v),
    },
    // M985: line keys this station has struck out.
    struckKeys: {
      type: Array,
      default: () => [],
    },
    // Tapping QTY strikes a row only on the Preparing tab.
    strikeable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["strike"],
  computed: {
    quantity() {
      return Number(this.item.quantity ?? this.item.qty ?? 1);
    },
    modifiers() {
      return this.item.modifiers ?? this.item.options ?? this.item.choices ?? [];
    },
    hasModifiers() {
      return this.modifiers.length > 0;
    },
    // Set/Choice items: BE tags each entry in `modifiers` with type
    // ("modifier" | "choice"). We split them so the template can render
    // Choices as primary item rows and any Main-level modifiers above them.
    choices() {
      return this.modifiers.filter((m) => m && m.type === "choice");
    },
    regularModifiers() {
      return this.modifiers.filter((m) => !m || m.type !== "choice");
    },
    // Prefer the BE flag, but fall back to a content check so callers that
    // don't go through the KDS endpoint still behave correctly.
    hasChoices() {
      return Boolean(this.item.has_choices) || this.choices.length > 0;
    },
    isVoided() {
      return Boolean(this.item.is_voided);
    },
    // MR196: the per-item note the customer typed (today only GrabFood sends
    // one — Grab's item "specifications"). Blank notes print nothing.
    remark() {
      const text = this.item.remark;
      return typeof text === "string" && text.trim() !== "" ? text.trim() : "";
    },
  },
  methods: {
    // A row is the item itself or, for a Set, one Choice — each carries the
    // BE's line_key. Voided rows never count toward Done, so they stay inert.
    canStrike(node) {
      return this.strikeable && !this.isVoided && Boolean(node?.line_key);
    },
    isStruck(node) {
      return Boolean(node?.line_key) && this.struckKeys.includes(node.line_key);
    },
    onQtyTap(event, node) {
      if (!this.canStrike(node)) return;
      // The chit itself selects on click; a strike must not also select it.
      event.stopPropagation();
      this.$emit("strike", node.line_key);
    },
    // Name 2-4 fall back to Name 1 (the BE does the same) so the kitchen never
    // sees a blank row — most items leave Name 4 empty.
    primaryTextOf(node) {
      if (!node) return "";
      return node[NAME_FIELD_KEYS[this.nameField]] || node.name || "";
    },
    // Only the Display-both toggle prints the second line; the picker says
    // which column it comes from. A name column falls back to Name 1 exactly
    // like the main line does, so the two can read alike when the chosen
    // column is blank — that is the configured behaviour, not a bug.
    descriptionOf(node) {
      if (!this.showDescription || !node) return "";
      const text =
        this.descriptionField === "none"
          ? node.description
          : node[NAME_FIELD_KEYS[this.descriptionField]] || node.name;
      return typeof text === "string" && text.trim() !== "" ? text : "";
    },
    // Per product call: when a Set has qty>1, the kitchen sees each Choice
    // multiplied — main_qty × choice_qty — so the qty pill reflects the
    // physical units to prepare, not the raw per-set selection.
    choiceQuantity(choice) {
      const choiceQty = Number(choice.quantity ?? 1) || 1;
      return this.quantity * choiceQty;
    },
  },
};
</script>

<style scoped>
.chit-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  /* M944: the qty box lines up with the "DONE" label in the chit footer.
     `.chit__items` already pads 24px; the footer pads 16px and the button
     another 24px, so the extra 16px here puts both at 40px from the card
     edge. The row divider stays at the 24px inset. */
  padding-left: 16px;
}

.chit-item__qty-box {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e0e3e5;
  color: #191c1e;
  border: 1px solid #c6c6cd;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  font-size: 24px;
  font-weight: 900;
  line-height: 24px;
}

.chit-item__qty-box--tappable {
  position: relative;
  padding: 0;
  font-family: inherit;
  cursor: pointer;
  touch-action: manipulation;
  transition: background-color 0.12s ease, transform 0.08s ease;
}

.chit-item__qty-box--tappable:active {
  transform: scale(0.94);
}

/* M985: struck row — filled green QTY with a check, name and modifiers
   crossed out. Kept apart from the grey voided look so the kitchen can tell
   "done" from "cancelled" across the room. */
.chit-item--struck .chit-item__qty-box {
  position: relative;
  background: #16a34a;
  border-color: #15803d;
  color: #fff;
}

.chit-item__qty-check {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  color: #16a34a;
  border: 2px solid #16a34a;
  font-size: 14px;
  line-height: 16px;
  font-weight: 900;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chit-item--struck .chit-item__name,
.chit-item--struck .chit-item__description,
.chit-item--struck .chit-item__modifiers {
  text-decoration: line-through;
  text-decoration-thickness: 2px;
  color: #76777d;
}

.chit-item--struck .chit-item__modifiers :deep(.chit-modifier__row),
.chit-item--struck .chit-item__modifiers :deep(.chit-modifier__description) {
  text-decoration: line-through;
  color: #76777d;
}

.chit-item__body {
  flex: 0 1 auto;
  min-width: 0;
}

.chit-item__name {
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  color: #191c1e;
  margin: 0 0 8px 0;
  word-break: break-word;
}

.chit-item__description {
  font-size: 15px;
  line-height: 20px;
  color: #45464d;
  margin: 0 0 8px 0;
  word-break: break-word;
}

.chit-item--voided .chit-item__description {
  text-decoration: line-through;
  color: #76777d;
}

/* MR196: item remark in red under the modifiers, as on the order email. */
.chit-item__remark {
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  color: #dc3545;
  margin: 8px 0 0 0;
  white-space: pre-line;
  word-break: break-word;
}

.chit-item--struck .chit-item__remark,
.chit-item--voided .chit-item__remark {
  text-decoration: line-through;
  color: #76777d;
}

.chit-item-choice-group__main-remark {
  /* Same content column as the Main-level modifiers above it. */
  padding-left: 80px;
  margin: 0;
}

.chit-item__modifiers {
  margin: 4px 0 0 8px;
  padding-left: 16px;
  border-left: 2px solid #c6c6cd;
}

.chit-item--voided .chit-item__name {
  text-decoration: line-through;
  text-decoration-thickness: 2px;
  color: #76777d;
}

.chit-item--voided .chit-item__qty-box {
  opacity: 0.5;
  text-decoration: line-through;
}

.chit-item--voided .chit-item__modifiers {
  opacity: 0.5;
}

/* Set/Choice layout — stacked rows inside one logical item entry. Spacing
   between rows mirrors `chit__items > chit-item + chit-item` so the visual
   rhythm matches a normal multi-item chit. */
.chit-item-choice-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chit-item-choice-group__main-modifiers {
  /* Aligns with chit-item__body's content column: the row's own 16px indent
     (M944) plus the 48px qty box and the 16px gap. */
  padding-left: 80px;
}

.chit-item-choice-group .chit-item + .chit-item {
  padding-top: 16px;
  border-top: 2px solid #e6e8ea;
}

.chit-item-choice-group.chit-item--voided .chit-item__name,
.chit-item-choice-group.chit-item--voided .chit-item__description,
.chit-item-choice-group.chit-item--voided .chit-item__qty-box,
.chit-item-choice-group.chit-item--voided .chit-item__modifiers,
.chit-item-choice-group.chit-item--voided .chit-item-choice-group__main-modifiers {
  opacity: 0.5;
  text-decoration: line-through;
}
</style>
